import { useState } from 'react';
import { PAGE_WRAPPER_STYLES } from 'src/styles/pageStyles';
import { cn } from 'src/lib/utils';
import {
  useAdminOrgManagementQuery,
  useAdminSearchOrganizationLazyQuery,
  useAdminBanOrganizationMutation,
  useAdminBanUserMutation,
} from 'src/generated/graphql';
import { BannedOrgsList } from './BannedOrgsList';
import { BannedEmailsList } from './BannedEmailsList';
import { ProvisioningSection } from './ProvisioningSection';
import { InvitationSection } from './InvitationSection';
import { TierChangeSection } from './TierChangeSection';
import { TrialExtensionSection } from './TrialExtensionSection';
import { Link } from 'react-router-dom';
import { routes } from 'src/routes';
import { formatDistanceToNow } from 'date-fns';
import { OrganizationTier } from 'src/generated/graphql';

export const OrgManagementPage = () => {
  const [searchSlug, setSearchSlug] = useState('');
  const [banReason, setBanReason] = useState('');
  const [showBanOrgConfirm, setShowBanOrgConfirm] = useState(false);
  const [banEmailInput, setBanEmailInput] = useState('');
  const [banEmailReason, setBanEmailReason] = useState('');

  const {
    data: managementData,
    loading: managementLoading,
    error: managementError,
    refetch: refetchManagement,
  } = useAdminOrgManagementQuery();

  const [
    searchOrg,
    { data: searchData, loading: searchLoading, error: searchError },
  ] = useAdminSearchOrganizationLazyQuery();

  const [banOrganization, { loading: banOrgLoading }] =
    useAdminBanOrganizationMutation({
      onCompleted: () => {
        setShowBanOrgConfirm(false);
        setBanReason('');
        refetchManagement();
        // Refresh search to show updated ban status
        if (searchSlug.trim()) {
          searchOrg({ variables: { slug: searchSlug.trim() } });
        }
      },
    });

  const [banUser, { loading: banUserLoading }] = useAdminBanUserMutation({
    onCompleted: () => {
      setBanEmailInput('');
      setBanEmailReason('');
      refetchManagement();
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchSlug.trim()) {
      searchOrg({ variables: { slug: searchSlug.trim() } });
    }
  };

  const handleBanOrg = () => {
    if (searchData?.organization) {
      banOrganization({
        variables: {
          organizationSlug: searchData.organization.slug,
          reason: banReason || undefined,
        },
      });
    }
  };

  const handleBanEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (banEmailInput.trim()) {
      banUser({
        variables: {
          email: banEmailInput.trim().toLowerCase(),
          reason: banEmailReason || undefined,
        },
      });
    }
  };

  const searchedOrg = searchData?.organization;

  return (
    <div className={cn('flex flex-col py-8 gap-8', PAGE_WRAPPER_STYLES)}>
      {/* Provision New Organization Section */}
      <ProvisioningSection />

      {/* Search Organizations Section */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-900">
          Search Organizations
        </h2>

        <form onSubmit={handleSearch} className="flex gap-3 mb-4">
          <input
            type="text"
            value={searchSlug}
            onChange={(e) => setSearchSlug(e.target.value)}
            placeholder="Enter organization slug..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 font-mono text-sm"
          />
          <button
            type="submit"
            disabled={searchLoading || !searchSlug.trim()}
            className="px-6 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
          >
            {searchLoading ? 'Searching...' : 'Search'}
          </button>
        </form>

        {searchError && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 text-sm">
            Organization not found or error: {searchError.message}
          </div>
        )}

        {searchedOrg && (
          <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <Link
                    to={routes.org.getPath({ orgSlug: searchedOrg.slug })}
                    className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {searchedOrg.name || '(unnamed)'}
                  </Link>
                  <span className="ml-3 text-sm text-gray-500 font-mono">
                    {searchedOrg.slug}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'px-2.5 py-0.5 rounded-full text-xs font-medium',
                      searchedOrg.tier === 'LEGACY_FREE'
                        ? 'bg-purple-100 text-purple-800'
                        : searchedOrg.tier === 'PRO_TRIAL'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-blue-100 text-blue-800'
                    )}
                  >
                    {searchedOrg.tier}
                  </span>
                  {searchedOrg.tier !== searchedOrg.effectiveTier && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Effective: {searchedOrg.effectiveTier}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div className="text-sm text-gray-600">
                Created{' '}
                {formatDistanceToNow(new Date(searchedOrg.dateCreated), {
                  addSuffix: true,
                })}
              </div>

              {searchedOrg.memberships.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Members ({searchedOrg.memberships.length})
                  </h4>
                  <div className="bg-gray-50 rounded-md p-3 space-y-1">
                    {searchedOrg.memberships.map((membership) => (
                      <div
                        key={membership.id}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="font-mono text-gray-600">
                          {membership.user.email}
                        </span>
                        <span className="text-xs text-gray-500 uppercase">
                          {membership.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invitation Management */}
              <InvitationSection
                organization={searchedOrg}
                onInvitationChange={() => {
                  searchOrg({ variables: { slug: searchedOrg.slug } });
                }}
              />

              {/* Tier Management */}
              <TierChangeSection
                organization={searchedOrg}
                onTierChange={() => {
                  searchOrg({ variables: { slug: searchedOrg.slug } });
                }}
              />

              {/* Trial Extension (only for PRO_TRIAL orgs) */}
              {searchedOrg.tier === OrganizationTier.ProTrial && (
                <TrialExtensionSection
                  organizationSlug={searchedOrg.slug}
                  trialEndsAt={searchedOrg.trialEndsAt ?? null}
                  isTrialExpired={searchedOrg.isTrialExpired}
                  onExtended={() => {
                    searchOrg({ variables: { slug: searchedOrg.slug } });
                  }}
                />
              )}

              {!showBanOrgConfirm ? (
                <button
                  onClick={() => setShowBanOrgConfirm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium text-sm"
                >
                  Ban Organization
                </button>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 space-y-3">
                  <div className="text-sm text-red-800 font-medium">
                    This will ban the organization and all{' '}
                    {searchedOrg.memberships.length} member email(s) from
                    creating new accounts.
                  </div>
                  <input
                    type="text"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Reason for ban (optional)"
                    className="w-full px-3 py-2 border border-red-300 rounded-md text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleBanOrg}
                      disabled={banOrgLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium text-sm disabled:opacity-50"
                    >
                      {banOrgLoading ? 'Banning...' : 'Confirm Ban'}
                    </button>
                    <button
                      onClick={() => {
                        setShowBanOrgConfirm(false);
                        setBanReason('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Ban Individual Email Section */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-900">
          Ban Individual Email
        </h2>

        <form onSubmit={handleBanEmail} className="space-y-3">
          <div className="flex gap-3">
            <input
              type="email"
              value={banEmailInput}
              onChange={(e) => setBanEmailInput(e.target.value)}
              placeholder="Enter email address..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 font-mono text-sm"
            />
          </div>
          <input
            type="text"
            value={banEmailReason}
            onChange={(e) => setBanEmailReason(e.target.value)}
            placeholder="Reason for ban (optional)"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm"
          />
          <button
            type="submit"
            disabled={banUserLoading || !banEmailInput.trim()}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
          >
            {banUserLoading ? 'Banning...' : 'Ban Email'}
          </button>
        </form>
      </section>

      {/* Loading/Error States */}
      {managementLoading ? (
        <div className="text-gray-500">Loading banned lists...</div>
      ) : managementError ? (
        <div className="text-red-500">
          Error loading data: {managementError.message}
        </div>
      ) : (
        <>
          {/* Banned Organizations List */}
          <section>
            <h2 className="text-lg font-semibold mb-4 text-gray-900">
              Banned Organizations (
              {managementData?.adminDashboard?.bannedOrganizations?.length ?? 0})
            </h2>
            <BannedOrgsList
              organizations={
                managementData?.adminDashboard?.bannedOrganizations ?? []
              }
              onUnban={refetchManagement}
            />
          </section>

          {/* Banned Emails List */}
          <section>
            <h2 className="text-lg font-semibold mb-4 text-gray-900">
              Banned Emails (
              {managementData?.adminDashboard?.bannedEmails?.length ?? 0})
            </h2>
            <BannedEmailsList
              emails={managementData?.adminDashboard?.bannedEmails ?? []}
              onUnban={refetchManagement}
            />
          </section>
        </>
      )}
    </div>
  );
};
